import { cx } from "@emotion/css";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MuiBox from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { type ComponentType, type ReactNode, useState } from "react";
import isMobile from "src/helpers/isMobile";

interface BoxProps {
	className?: string;
	headerClassName?: string;
	tabsClassName?: string;
	tabsTitle?: string;
	tabsLabel?: string;
	expandable?: boolean;
	Header?: ReactNode;
	children?: ReactNode;
	showAllLink?: string;
	selectedTab?: number;
	tabs?: ReactNode[];
	tabTestIds?: string[];
	TabsWrapper?: ComponentType<{ children: ReactNode }>;
	onTabsChange?: (value: number) => void;
	renderActions?: () => ReactNode;
	additionalTabsActions?: () => ReactNode;
	OpenIcon?: ComponentType;
}

function Box(props: BoxProps) {
	const [isOpen, setIsOpen] = useState(!(props.expandable && isMobile()));

	const renderTabs = () => {
		return (
			<Tabs
				value={props.selectedTab}
				textColor="primary"
				onChange={(_event, newValue) => props.onTabsChange?.(newValue)}
				variant="scrollable"
				className="tabs"
			>
				{props.tabs?.map((tab, index) => (
					<Tab
						className="tab size-16"
						label={<span className="first-letter-upper">{tab}</span>}
						key={index}
						data-test-id={props.tabTestIds?.[index]}
					/>
				))}
			</Tabs>
		);
	};

	const renderActions = () => {
		return props.renderActions && <div>{props.renderActions()}</div>;
	};

	const renderChildren = () => {
		return isOpen && props.children;
	};

	const renderOpenIcon = () => {
		if (props.OpenIcon) {
			const Icon = props.OpenIcon;

			return <Icon />;
		}

		return !isOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />;
	};

	const renderOpenIconButton = () => {
		return (
			shouldRenderIsOpen() && (
				<IconButton size="small" color="secondary" onClick={toggleIsOpen}>
					{renderOpenIcon()}
				</IconButton>
			)
		);
	};

	const shouldRenderIsOpen = () => {
		return props.expandable && isMobile();
	};

	const toggleIsOpen = () => {
		return setIsOpen(!isOpen);
	};

	const renderTabsComponent = () => {
		if ((!props.tabs || !props.tabs.length) && !props.additionalTabsActions) {
			return null;
		}

		const Wrapper = props.TabsWrapper || "div";

		return (
			<div className="flex flex-col gap-8 w-100">
				{props.tabsTitle && (
					<span className="size-18 text-bold">{props.tabsTitle}</span>
				)}
				<div className={cx(props.tabsClassName, "basic-box--tabs")}>
					{props.tabsLabel && (
						<span className="size-14 text-grey flex-center-row mr-8">
							{props.tabsLabel}
						</span>
					)}
					<Wrapper>{renderTabs()}</Wrapper>
					{props.additionalTabsActions?.()}
				</div>
			</div>
		);
	};

	const renderHeader = () => {
		if (
			!props.Header &&
			!props.renderActions &&
			!props.tabs &&
			!props.expandable
		) {
			return null;
		}

		return (
			<div
				className={cx(props.headerClassName, "basic-box--header")}
				onClick={shouldRenderIsOpen() ? toggleIsOpen : undefined}
			>
				{props.Header}
				{renderTabsComponent()}
				{renderActions()}
				{renderOpenIconButton()}
			</div>
		);
	};

	return (
		<MuiBox className={cx(props.className, "basic-box")}>
			{renderHeader()}
			{renderChildren()}
		</MuiBox>
	);
}

export default Box;
